<script setup>
import { computed, onMounted, onBeforeUnmount, reactive, ref } from "vue";
import { Plus, Search, Pencil, Power, Wifi, WifiOff } from "@lucide/vue";
import { api } from "../api";
import { useSessionStore } from "../stores/session";
import PageHeader from "../components/PageHeader.vue";
import TablePager from "../components/TablePager.vue";
import UiDrawer from "../ui/UiDrawer.vue";
import UiSelect from "../ui/UiSelect.vue";
import UiToast from "../ui/UiToast.vue";
import UiButton from "../ui/UiButton.vue";
import UiConfirmDrawer from "../ui/UiConfirmDrawer.vue";
const session = useSessionStore(),
  rows = ref([]),
  search = ref(""),
  page = ref(1),
  modal = ref(false),
  toast = ref(""),
  toastType = ref("success"),
  saving = ref(false),
  deleting = ref(false),
  deleteTarget = ref(null),
  perPage = 15;
const blank = () => ({
    id: null,
    schoolId: session.selectedSchoolId || session.user?.school_id || "",
    name: "",
    deviceKey: "",
    location: "",
    mode: "entry",
    status: "active",
  }),
  form = reactive(blank());
const schoolOptions = computed(() => session.schools.map((school) => ({
  value: String(school.id), label: school.name, description: school.code || "Erva Spor Okulu",
})));
const modeOptions = [
  { value: "entry", label: "Giriş", description: "Varsayılan geçiş yönü giriş" },
  { value: "exit", label: "Çıkış", description: "Varsayılan geçiş yönü çıkış" },
];
const statusOptions = [
  { value: "active", label: "Aktif", description: "Cihaz istekleri kabul edilir" },
  { value: "disabled", label: "Pasif", description: "Cihaz erişimi durdurulur" },
];
const filtered = computed(() =>
    rows.value.filter((r) =>
      `${r.name} ${r.school_name || ""} ${r.device_key} ${r.location || ""}`
        .toLowerCase()
        .includes(search.value.toLowerCase()),
    ),
  ),
  schoolDeviceCounts = computed(() =>
    rows.value.reduce((counts, device) => {
      const key = String(device.school_id);
      counts[key] = (counts[key] || 0) + 1;
      return counts;
    }, {}),
  ),
  paged = computed(() =>
    filtered.value.slice((page.value - 1) * perPage, page.value * perPage),
  );
function online(r) {
  return r.last_seen && Date.now() - new Date(r.last_seen).getTime() < 120000;
}
function show(m, t = "success") {
  toast.value = m;
  toastType.value = t;
  setTimeout(() => (toast.value = ""), 3000);
}
async function load() {
  try {
    const q = session.selectedSchoolId
      ? `?schoolId=${session.selectedSchoolId}`
      : "";
    rows.value = (await api(`/api/devices${q}`)).devices || [];
  } catch (e) {
    show(e.message, "error");
  }
}
function open(r = null) {
  Object.assign(form, blank());
  if (r)
    Object.assign(form, {
      id: r.id,
      schoolId: String(r.school_id),
      name: r.name,
      deviceKey: r.device_key,
      location: r.location || "",
      mode: r.mode,
      status: r.status,
    });
  modal.value = true;
}
async function save() {
  saving.value = true;
  try {
    await api(form.id ? `/api/devices/${form.id}` : "/api/devices", {
      method: form.id ? "PUT" : "POST",
      body: JSON.stringify(form),
    });
    modal.value = false;
    show("Cihaz kaydedildi.");
    await load();
  } catch (e) {
    show(e.message, "error");
  } finally {
    saving.value = false;
  }
}
async function disable(r) {
  deleteTarget.value = r;
}
async function confirmDisable() {
  if (!deleteTarget.value) return;
  deleting.value = true;
  try {
    await api(`/api/devices/${deleteTarget.value.id}`, { method: "DELETE" });
    show("Cihaz pasif yapıldı.");
    deleteTarget.value = null;
    await load();
  } catch (e) {
    show(e.message, "error");
  } finally {
    deleting.value = false;
  }
}
const dt = (v) =>
  v
    ? new Intl.DateTimeFormat("tr-TR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(v))
    : "Henüz bağlanmadı";
const refresh = () => load();
onMounted(() => {
  load();
  window.addEventListener("lopiy:refresh", refresh);
});
onBeforeUnmount(() => window.removeEventListener("lopiy:refresh", refresh));
</script>
<template>
  <PageHeader
    title="Cihazlar"
    description="Erva Spor Okulları turnike terminallerini ve bağlantı durumlarını izleyin."
    ><button class="btn-primary" @click="open()">
      <Plus class="size-4" />Cihaz Ekle
    </button></PageHeader
  >
  <section class="panel overflow-hidden">
    <div class="toolbar">
      <span class="text-sm font-semibold text-slate-500"
        >{{ filtered.length }} cihaz</span
      ><label class="relative w-full sm:w-72"
        ><Search class="absolute left-3 top-2.5 size-4 text-slate-400" /><input
          v-model="search"
          class="input pl-9"
          placeholder="Cihaz, okul, anahtar veya konum ara"
      /></label>
    </div>
    <div class="overflow-x-auto">
      <table class="data-table">
        <thead>
          <tr>
            <th>Cihaz</th>
            <th>Okul</th>
            <th>Konum</th>
            <th>Anahtar</th>
            <th>Son bağlantı</th>
            <th>Durum</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in paged" :key="r.id">
            <td>
              <div class="flex items-center gap-2">
                <component
                  :is="online(r) ? Wifi : WifiOff"
                  :class="[
                    'size-4',
                    online(r) ? 'text-emerald-600' : 'text-slate-400',
                  ]"
                /><strong class="text-ink-900">{{ r.name }}</strong>
              </div>
            </td>
            <td>
              <strong class="block text-ink-900">{{ r.school_name }}</strong>
              <span class="text-xs text-slate-500">
                Bu okulda {{ schoolDeviceCounts[String(r.school_id)] || 0 }} cihaz
              </span>
            </td>
            <td>{{ r.location || "-" }}</td>
            <td class="font-mono text-xs">{{ r.device_key }}</td>
            <td class="whitespace-nowrap">{{ dt(r.last_seen) }}</td>
            <td>
              <span
                :class="r.status === 'active' ? 'chip-success' : 'chip-neutral'"
                >{{ r.status === "active" ? "Aktif" : "Pasif" }}</span
              >
            </td>
            <td>
              <div class="flex justify-end gap-1">
                <button class="icon-btn size-8" @click="open(r)">
                  <Pencil class="size-3.5" /></button
                ><button
                  class="icon-btn size-8 hover:text-red-600"
                  @click="disable(r)"
                >
                  <Power class="size-3.5" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <TablePager
      v-model:page="page"
      :total="filtered.length"
      :per-page="perPage"
    />
  </section>
  <UiDrawer
    :open="modal"
    :title="form.id ? 'Cihazı düzenle' : 'Yeni cihaz oluştur'"
    description="Turnike terminalinin okul, konum ve çalışma ayarlarını belirleyin."
    size="md"
    @close="modal = false"
    ><form id="device-form" class="grid gap-5 sm:grid-cols-2" @submit.prevent="save">
      <label v-if="session.isSuper" class="field sm:col-span-2"
        ><span class="field-label">Okul</span
        ><UiSelect v-model="form.schoolId" :options="schoolOptions" searchable search-placeholder="Okul ara..." /></label
      ><label class="field"
        ><span class="field-label">Cihaz adı</span
        ><input v-model="form.name" class="input" required /></label
      ><label class="field"
        ><span class="field-label">Konum</span
        ><input v-model="form.location" class="input" /></label
      ><label class="field sm:col-span-2"
        ><span class="field-label">Cihaz anahtarı</span
        ><input
          v-model="form.deviceKey"
          class="input font-mono"
          placeholder="Boş bırakılırsa otomatik üretilir" /></label
      ><label class="field"
        ><span class="field-label">Mod</span
        ><UiSelect v-model="form.mode" :options="modeOptions" /></label
      ><label class="field"
        ><span class="field-label">Durum</span
        ><UiSelect v-model="form.status" :options="statusOptions" /></label
      >
    </form>
    <template #footer>
      <UiButton variant="secondary" @click="modal = false">Vazgeç</UiButton>
      <UiButton type="submit" form="device-form" :loading="saving">Cihazı Kaydet</UiButton>
    </template>
  </UiDrawer>
  <UiConfirmDrawer
    :open="Boolean(deleteTarget)"
    title="Cihazı pasife al"
    :description="deleteTarget ? `${deleteTarget.name} cihazının API erişimi durdurulacak.` : ''"
    confirm-label="Cihazı Pasife Al"
    :loading="deleting"
    @close="deleteTarget = null"
    @confirm="confirmDisable"
  />
  <UiToast :message="toast" :type="toastType" @close="toast = ''" />
</template>
